// How the check was done, for the context add-on (PLAN-Device-Context.md
// §B4.5): time on the consent page, and per attempt its duration, time spent
// with the tab/app hidden, orientation changes and — on Android only, where no
// permission prompt is needed — a motion summary (variance only, no raw data).
// A phone held in a hand always moves a little; a propped phone or an emulator
// doesn't.

export interface BehaviourReport {
  consentDwellMs?: number;
  durationMs?: number;
  hiddenMs?: number;
  visibilityChanges?: number;
  orientationChanges?: number;
  motion?: { samples: number; accelVariance?: number; rotationVariance?: number };
}

let consentShownAt: number | undefined;

export function markConsentShown(): void {
  consentShownAt ??= performance.now();
}

export function consentDwellMs(): number | undefined {
  return consentShownAt === undefined ? undefined : Math.round(performance.now() - consentShownAt);
}

// Welford's online variance, so nothing but three numbers is kept per signal.
class RunningVariance {
  n = 0;
  private mean = 0;
  private m2 = 0;
  add(x: number) {
    this.n += 1;
    const d = x - this.mean;
    this.mean += d / this.n;
    this.m2 += d * (x - this.mean);
  }
  get variance(): number | undefined {
    return this.n > 1 ? this.m2 / (this.n - 1) : undefined;
  }
}

const MAX_MOTION_SAMPLES = 3000;

function motionAllowedWithoutPrompt(): boolean {
  // iOS exposes DeviceMotionEvent.requestPermission and would need a prompt; skip it there.
  const dme = (window as unknown as { DeviceMotionEvent?: { requestPermission?: unknown } }).DeviceMotionEvent;
  return !!dme && typeof dme.requestPermission !== 'function';
}

let stopCurrent: (() => BehaviourReport) | null = null;

/** Start measuring an attempt. Any attempt still running is discarded. */
export function startAttempt(withMotion: boolean): void {
  stopCurrent?.();
  const startedAt = performance.now();
  let hiddenSince = document.hidden ? startedAt : undefined;
  let hiddenMs = 0;
  let visibilityChanges = 0;
  let orientationChanges = 0;
  const accel = new RunningVariance();
  const rotation = new RunningVariance();

  const onVisibility = () => {
    visibilityChanges += 1;
    const now = performance.now();
    if (document.hidden) {
      hiddenSince = now;
    } else if (hiddenSince !== undefined) {
      hiddenMs += now - hiddenSince;
      hiddenSince = undefined;
    }
  };
  const onOrientation = () => {
    orientationChanges += 1;
  };
  const onMotion = (e: DeviceMotionEvent) => {
    if (accel.n >= MAX_MOTION_SAMPLES) return;
    const a = e.acceleration || e.accelerationIncludingGravity;
    if (a && a.x !== null && a.y !== null && a.z !== null) accel.add(Math.hypot(a.x, a.y, a.z));
    const r = e.rotationRate;
    if (r && r.alpha !== null && r.beta !== null && r.gamma !== null) rotation.add(Math.hypot(r.alpha, r.beta, r.gamma));
  };

  const motion = withMotion && motionAllowedWithoutPrompt();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('orientationchange', onOrientation);
  if (motion) window.addEventListener('devicemotion', onMotion);

  stopCurrent = () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('orientationchange', onOrientation);
    if (motion) window.removeEventListener('devicemotion', onMotion);
    const now = performance.now();
    if (hiddenSince !== undefined) hiddenMs += now - hiddenSince;
    stopCurrent = null;
    return {
      durationMs: Math.round(now - startedAt),
      hiddenMs: Math.round(hiddenMs),
      visibilityChanges,
      orientationChanges,
      motion: accel.n
        ? {
            samples: accel.n,
            accelVariance: accel.variance,
            rotationVariance: rotation.variance,
          }
        : undefined,
    };
  };
}

/** Stop the running attempt and return its measurements (undefined if none). */
export function finishAttempt(): BehaviourReport | undefined {
  return stopCurrent?.();
}
